import { FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { Gateway, ReportAggregatesFilters, ReportGroupByKey, ReportPeriod } from '@/api/models';
import { DateRangeFields } from './DateRangeFields';
import { EnumMultiSelect } from './EnumMultiSelect';
import { TagsField } from './TagsField';

const GATEWAY_VALUES: Gateway[] = ['redsys', 'stripe', 'adyen', 'payten', 'plexo'];
/** What the API groups by when `groupBy` is omitted — `tag` is deliberately not part of it. */
const DEFAULT_GROUP_BY: ReportGroupByKey[] = ['application', 'tenant', 'gateway', 'period'];
const GROUP_BY_VALUES: ReportGroupByKey[] = [...DEFAULT_GROUP_BY, 'tag'];
const PERIOD_VALUES: ReportPeriod[] = ['day', 'week', 'month'];
const TENANT_TAG_PATTERN = /^[a-z0-9_-]+:.+$/;

interface AggregatesFiltersFormProps {
  filters: ReportAggregatesFilters;
  onChange: (patch: Partial<ReportAggregatesFilters>) => void;
}

/** Every query parameter `GET /v1/admin/reports/aggregates` accepts, and no more. */
export function AggregatesFiltersForm({ filters, onChange }: AggregatesFiltersFormProps) {
  const translate = useTranslate();
  const groupBy = filters.groupBy ?? DEFAULT_GROUP_BY;

  return (
    <Stack spacing={2.5}>
      <DateRangeFields
        fromLabel={translate('pay.operations.filters.dateFrom')}
        toLabel={translate('pay.operations.filters.dateTo')}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onChangeFrom={(dateFrom) => onChange({ dateFrom })}
        onChangeTo={(dateTo) => onChange({ dateTo })}
      />
      <TagsField
        label={translate('pay.operations.filters.tenantCode')}
        helperText={translate('pay.operations.filters.tenantCodeHelp')}
        values={filters.tenantCode ?? []}
        onChange={(tenantCode) => onChange({ tenantCode: tenantCode.length > 0 ? tenantCode : undefined })}
      />
      <TagsField
        label={translate('pay.operations.filters.tenantTag')}
        helperText={translate('pay.operations.filters.tenantTagHelp')}
        values={filters.tenantTag ?? []}
        validate={(value) => TENANT_TAG_PATTERN.test(value)}
        onChange={(tenantTag) => onChange({ tenantTag: tenantTag.length > 0 ? tenantTag : undefined })}
      />
      <TagsField
        label={translate('pay.operations.filters.applicationCode')}
        values={filters.applicationCode ?? []}
        onChange={(applicationCode) =>
          onChange({ applicationCode: applicationCode.length > 0 ? applicationCode : undefined })
        }
      />
      <EnumMultiSelect
        label={translate('pay.operations.filters.gateway')}
        options={GATEWAY_VALUES.map((value) => ({ value, label: value }))}
        values={filters.gateway ?? []}
        onChange={(values) => onChange({ gateway: values.length > 0 ? (values as Gateway[]) : undefined })}
      />
      <EnumMultiSelect
        label={translate('pay.operations.aggregates.groupByLabel')}
        options={GROUP_BY_VALUES.map((value) => ({ value, label: translate(`pay.operations.groupBy.${value}`) }))}
        values={groupBy}
        onChange={(values) =>
          onChange({ groupBy: values.length > 0 ? (values as ReportGroupByKey[]) : DEFAULT_GROUP_BY })
        }
      />
      {groupBy.includes('period') && (
        <FormControl size="small" fullWidth>
          <InputLabel>{translate('pay.operations.aggregates.periodLabel')}</InputLabel>
          <Select
            value={filters.period ?? 'month'}
            label={translate('pay.operations.aggregates.periodLabel')}
            onChange={(event) => onChange({ period: event.target.value as ReportPeriod })}
          >
            {PERIOD_VALUES.map((value) => (
              <MenuItem key={value} value={value}>
                {translate(`pay.operations.period.${value}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  );
}
